import {
  getDestinationType,
  getDestinationUrl,
  getNearestRegion,
  getElementPosition,
  getNonHiddenInputCount,
} from './utils';

const useTracking = ({ debug = false }) => {
  const trackers = [];

  // Add a tracker
  // Trackers must have a track method
  const addTracker = (tracker) => {
    if (tracker?.track) {
      trackers.push(tracker);
    }
  };

  // Sort data and remove empty values
  const sortData = (data) =>
    Object.keys(data)
      .sort()
      .reduce((obj, key) => {
        if (data[key]) {
          obj[key] = data[key];
        }
        return obj;
      }, {});

  const track = (data) => {
    const sortedData = sortData(data);
    trackers.forEach((tracker) => {
      tracker.track(sortedData, debug);
    });
  };

  // Track form events
  const trackForm = (form, props = {}) => {
    track({
      name: form.dataset.name || form.id || 'Default',
      type: form.dataset.type || 'Default',
      step_number: form.dataset.step,
      region: getNearestRegion(form).name,
      input_count: getNonHiddenInputCount(form),
      offer: form.dataset.offer,
      variant: form.dataset.variant,
      ...props,
    });
  };

  // Track form submissions
  const trackFormSubmit = (form, props = {}) => {
    trackForm(form, {
      event: 'form_submit',
      ...props,
    });
  };

  // Track form submissions
  const trackFormConversion = (form, props = {}) => {
    trackForm(form, {
      event: 'form_conversion',
      ...props,
    });
  };

  // Track form errors
  const trackFormError = (form, props = {}) => {
    trackForm(form, {
      event: 'form_error',
      ...props,
    });
  };

  // Track form steps
  const trackFormStep = (form, props = {}) => {
    trackForm(form, {
      event: 'form_step_submit',
      ...props,
    });
  };

  // Track link click
  const trackLinkClick = (link, props = {}, selector) => {
    const region = getNearestRegion(link);
    track({
      event: 'cta_click',
      destination_url: getDestinationUrl(link),
      destination_type: getDestinationType(link),
      region: region.name,
      position: getElementPosition(link, region.element, selector),
      offer: link.innerText,
      variant: link.dataset.variant,
      ...props,
    });
  };

  // Track link clicks
  const trackLinkClicks = (selector) => {
    document.addEventListener('click', (event) => {
      const link = event.target.closest(selector);

      // Ignore if link is already tracked
      if (!link || link?.dataset.tracked) {
        return;
      }

      // Mark link as tracked
      link.dataset.tracked = true;

      trackLinkClick(link, {}, selector);
    });
  };

  // Track link impression
  const trackLinkImpression = (link, props = {}, selector) => {
    trackLinkClick(
      link,
      {
        event: 'cta_view',
        ...props,
      },
      selector
    );
  };

  // Track link impressions
  const trackLinkImpressions = (selector) => {
    const links = document.querySelectorAll(selector);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Ignore if link is already tracked or not more than 50% visible
          if (entry.target.dataset.viewed || entry.intersectionRatio < 0.5) {
            return;
          }

          // Mark link as tracked
          entry.target.dataset.viewed = true;

          // Track impression
          trackLinkImpression(entry.target, {}, selector);

          // Stop observing the link
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.5, // Trigger when element is 50% visible
      }
    );

    links.forEach((link) => {
      observer.observe(link);
    });
  };

  // Track language change
  const trackLanguageChange = (language, props = {}) => {
    track({
      event: 'language_change',
      language,
      ...props,
    });
  };

  return {
    track,
    addTracker,
    trackFormSubmit,
    trackFormConversion,
    trackFormError,
    trackFormStep,
    trackLinkClick,
    trackLinkClicks,
    trackLinkImpression,
    trackLinkImpressions,
    trackLanguageChange,
  };
};

export default useTracking;
